import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  auth, 
  db 
} from '../firebase/config';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileCompleted, setProfileCompleted] = useState(false);

  // Normalize profile fields to canonical values and remove legacy defaults
  const normalizeProfileData = (data) => {
    if (!data) return data;

    const legacyCollegeMap = {
      'VJTI': 'VJTI Mumbai',
      'COEP': 'COEP Pune',
      'SPIT': 'SPIT Mumbai'
    };

    const allowedColleges = new Set([
      'VJTI Mumbai',
      'COEP Pune',
      'SPIT Mumbai',
      'PICT Pune',
      'VIT Pune',
      'DY Patil',
      'Other'
    ]);

    const allowedSubjects = new Set(['Mathematics', 'Physics', 'Chemistry']);

    const normalizedTargetColleges = Array.isArray(data.targetColleges)
      ? Array.from(new Set(
          data.targetColleges
            .map((c) => legacyCollegeMap[c] || c)
            .filter((c) => allowedColleges.has(c))
        ))
      : [];

    const normalizedPreferredSubjects = Array.isArray(data.preferredSubjects)
      ? Array.from(new Set(data.preferredSubjects.filter((s) => allowedSubjects.has(s))))
      : [];

    return {
      ...data,
      targetColleges: normalizedTargetColleges,
      preferredSubjects: normalizedPreferredSubjects
    };
  };

  // Google Sign In
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      console.log('Google sign in successful:', result.user.uid);
      
      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      console.log('User document exists:', userDoc.exists());
      
      if (!userDoc.exists()) {
        console.log('Creating new user profile...');
        // Create new user profile
        await setDoc(doc(db, 'users', result.user.uid), {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          targetExam: 'MH-CET',
          profileCompleted: false,
          createdAt: new Date(),
          lastActiveAt: new Date()
        });
        
        // Create user progress document with NEW structure
        await setDoc(doc(db, 'userProgress', result.user.uid), {
          userId: result.user.uid,
          currentPath: {
            currentNodeId: null, // Will be set to first available lesson
            completedNodes: [],
            nextNodeId: null,
            currentSubject: 'Mathematics',
            pathProgress: 0
          },
          questionHistory: {
            askedQuestions: [],
            questionPerformance: {}, // questionId -> { correct, responseTime, date }
            lastAskedDate: null,
            totalQuestionsAnswered: 0,
            correctAnswers: 0,
            averageResponseTime: 0
          },
          mastery: {}, // conceptId -> { level, lastReviewed, nextReview }
          sessionStats: {
            totalSessions: 0,
            totalTime: 0,
            averageScore: 0,
            lastSessionDate: null,
            averageSessionLength: 0
          },
          performance: {
            subjectAccuracy: {
              Mathematics: { correct: 0, total: 0, accuracy: 0 },
              Physics: { correct: 0, total: 0, accuracy: 0 },
              Chemistry: { correct: 0, total: 0, accuracy: 0 }
            },
            weakAreas: [],
            strongAreas: [],
            difficultyLevel: 'beginner'
          },
          preferences: {
            sessionLength: 16, // minutes
            difficulty: 'adaptive',
            subjects: ['Mathematics', 'Physics', 'Chemistry'],
            examDate: new Date('2026-04-09T00:00:00')
          },
          srsData: {}, // questionId -> { level, eFactor, intervalDays, repetitionCount, nextReview, lastReviewed }
          totalXP: 0,
          level: 1,
          streak: 0,
          lastStudyDate: null,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        
        console.log('User profile and progress created successfully');
        toast.success('Welcome to CET Master Pro! 🎉');
      } else {
        console.log('User already exists, checking profile completion...');
        const userData = userDoc.data();
        console.log('Existing user data:', userData);
        toast.success('Welcome back! 👋');
      }
      
    } catch (error) {
      console.error('Google sign in error:', error);
      toast.error('Failed to sign in with Google');
    }
  };

  // Phone OTP Sign In
  const signInWithPhone = async (phoneNumber) => {
    try {
      // Create recaptcha verifier
      const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible'
      });
      
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      
      return confirmationResult;
    } catch (error) {
      console.error('Phone sign in error:', error);
      toast.error('Failed to send OTP');
      throw error;
    }
  };

  // Verify OTP
  const verifyOTP = async (confirmationResult, otp) => {
    try {
      const result = await confirmationResult.confirm(otp);
      
      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        // Create new user profile
        await setDoc(doc(db, 'users', result.user.uid), {
          uid: result.user.uid,
          phoneNumber: result.user.phoneNumber,
          targetExam: 'MH-CET',
          profileCompleted: false,
          createdAt: new Date(),
          lastActiveAt: new Date()
        });
        
        // Create user progress document (same as Google sign in)
        await setDoc(doc(db, 'userProgress', result.user.uid), {
          userId: result.user.uid,
          subjects: {
            Mathematics: {
              progress: 0,
              chaptersCompleted: 0,
              conceptsCompleted: 0,
              questionsAnswered: 0,
              correctAnswers: 0,
              averageTime: 0,
              lastActive: new Date(),
              weakAreas: [],
              strongAreas: []
            },
            Physics: {
              progress: 0,
              chaptersCompleted: 0,
              conceptsCompleted: 0,
              questionsAnswered: 0,
              correctAnswers: 0,
              averageTime: 0,
              lastActive: new Date(),
              weakAreas: [],
              strongAreas: []
            },
            Chemistry: {
              progress: 0,
              chaptersCompleted: 0,
              conceptsCompleted: 0,
              questionsAnswered: 0,
              correctAnswers: 0,
              averageTime: 0,
              lastActive: new Date(),
              weakAreas: [],
              strongAreas: []
            }
          },
          achievements: [],
          stats: {
            totalXP: 0,
            currentLevel: 1,
            streakDays: 0,
            totalStudyTime: 0,
            questionsAnswered: 0,
            correctAnswers: 0,
            averageAccuracy: 0,
            fastestSolveTime: 0,
            longestStreak: 0
          },
          preferences: {
            notifications: {
              dailyReminder: true,
              streakAlerts: true,
              achievementAlerts: true,
              studyReminders: true
            },
            theme: 'light',
            soundEffects: true,
            animations: true
          }
        });
        
        toast.success('Welcome to CET Master Pro! 🎉');
      } else {
        toast.success('Welcome back! 👋');
      }
      
    } catch (error) {
      console.error('OTP verification error:', error);
      toast.error('Invalid OTP');
      throw error;
    }
  };

  // Sign Out
  const logout = async () => {
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      if (!user) {
        console.error('No user found for profile update');
        toast.error('No user found for profile update');
        return;
      }
      
      console.log('Updating profile for user:', user.uid);
      console.log('Profile data (raw):', profileData);

      // Normalize incoming data to avoid saving legacy defaults
      const normalizedProfile = normalizeProfileData(profileData);
      console.log('Profile data (normalized):', normalizedProfile);
      
      // Check if user document exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log('User document does not exist, creating new profile...');
        // Create new user profile
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          targetExam: 'MH-CET',
          ...normalizedProfile,
          profileCompleted: true,
          createdAt: new Date(),
          lastActiveAt: new Date()
        });
      } else {
        console.log('User document exists, updating profile...');
        // Update existing user profile
        await updateDoc(userDocRef, {
          ...normalizedProfile,
          profileCompleted: true,
          lastActiveAt: new Date()
        });
      }
      
      // Optimistically update local user state so UI reflects changes immediately
      setUser((prev) => ({
        ...prev,
        ...normalizedProfile,
        profileCompleted: true
      }));

      setProfileCompleted(true);
      toast.success('Profile updated successfully!');
      console.log('Profile updated successfully');
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
    }
  };

  // Delete all user data from Firebase (for development/testing)
  const deleteUserData = async (userId) => {
    try {
      console.log('Deleting all data for user:', userId);
      
      // List of collections that might contain user data
      const collectionsToCheck = [
        'users',
        'userProgress',
        'questionHistory',
        'sessions',
        'mockTests',
        'achievements',
        'notifications'
      ];
      
      let deletedCount = 0;
      
      for (const collectionName of collectionsToCheck) {
        try {
          // Try to delete the document directly (for collections where userId is the document ID)
          await deleteDoc(doc(db, collectionName, userId));
          console.log(`Deleted document from ${collectionName}`);
          deletedCount++;
        } catch (error) {
          // If direct deletion fails, try to find documents by userId field
          try {
            const q = query(collection(db, collectionName), where('userId', '==', userId));
            const querySnapshot = await getDocs(q);
            
            for (const docSnapshot of querySnapshot.docs) {
              await deleteDoc(docSnapshot.ref);
              console.log(`Deleted document ${docSnapshot.id} from ${collectionName}`);
              deletedCount++;
            }
          } catch (subError) {
            console.log(`No documents found in ${collectionName} for user ${userId}`);
          }
        }
      }
      
      console.log(`Successfully deleted ${deletedCount} documents for user ${userId}`);
      toast.success(`Deleted ${deletedCount} documents for user ${userId}`);
      
      return deletedCount;
    } catch (error) {
      console.error('Error deleting user data:', error);
      toast.error('Failed to delete user data');
      throw error;
    }
  };

  // Delete current user's data (convenience function)
  const deleteCurrentUserData = async () => {
    if (!user) {
      toast.error('No user logged in');
      return;
    }
    return await deleteUserData(user.uid);
  };

  // Make functions available globally for console access (development only)
  if (process.env.NODE_ENV === 'development') {
    window.deleteUserData = deleteUserData;
    window.deleteCurrentUserData = deleteCurrentUserData;
    
    // Additional helpful functions
    window.getCurrentUser = () => user;
    window.getUserData = async (userId) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        return userDoc.exists() ? userDoc.data() : null;
      } catch (error) {
        console.error('Error fetching user data:', error);
        return null;
      }
    };
    window.listAllUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'users'));
        const users = [];
        querySnapshot.forEach((doc) => {
          users.push({ id: doc.id, ...doc.data() });
        });
        console.table(users);
        return users;
      } catch (error) {
        console.error('Error listing users:', error);
        return [];
      }
    };
    
    console.log('🎯 Development functions available (DEV MODE ONLY):');
    console.log('📝 User Management:');
    console.log('  - window.deleteUserData(userId) - Delete data for specific user');
    console.log('  - window.deleteCurrentUserData() - Delete data for current user');
    console.log('📊 User Information:');
    console.log('  - window.getCurrentUser() - Get current user object');
    console.log('  - window.getUserData(userId) - Get specific user data');
    console.log('  - window.listAllUsers() - List all users in table format');
  }

  // Listen for auth state changes
  useEffect(() => {
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Current URL:', window.location.href);
    console.log('Firebase Auth Domain:', auth.config.authDomain);
    
    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('Auth state loading timeout - forcing loading to false');
      setLoading(false);
    }, 10000); // 10 second timeout

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'User logged out');
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);
      
      if (user) {
        console.log('User details:', { uid: user.uid, email: user.email });
        
        try {
          // Get user profile from Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          console.log('Firestore user doc exists:', userDoc.exists());
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('User data from Firestore (raw):', userData);

            // Normalize any legacy values in stored data
            const normalizedUserData = normalizeProfileData(userData);
            if (JSON.stringify(normalizedUserData) !== JSON.stringify(userData)) {
              try {
                await updateDoc(userDocRef, normalizedUserData);
                console.log('Normalized user profile written back to Firestore');
              } catch (normErr) {
                console.warn('Failed to write normalized profile:', normErr);
              }
            }

            setUser({ ...user, ...normalizedUserData });
            setProfileCompleted(normalizedUserData.profileCompleted || false);
            console.log('Profile completed set to:', normalizedUserData.profileCompleted || false);
          } else {
            console.log('No Firestore document found, setting profileCompleted to false');
            setUser(user);
            setProfileCompleted(false);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(user);
          setProfileCompleted(false);
        }
      } else {
        console.log('No user, resetting state');
        setUser(null);
        setProfileCompleted(false);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const value = {
    user,
    loading,
    profileCompleted,
    signInWithGoogle,
    signInWithPhone,
    verifyOTP,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
